'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';

// Each card shows its `poster` image immediately — no video data needs to be fetched at all
// for the thumbnail. The <video> element (below, used when `poster` is absent) is the fallback
// for a future testimonial without one yet: only the first two mount immediately, the rest
// lazily as they scroll into the track's viewport (see the IntersectionObserver below).
interface VideoTestimonial {
  name: string;
  designation: string;
  poster?: string;
  videoSrc: string;
}

// Ordered smallest video file first (checked via each URL's Content-Length) — the first two
// entries get preload="auto" below, so putting the two smallest clips there means that eager
// load is as cheap as possible instead of front-loading whichever happened to be listed first.
const VIDEO_TESTIMONIALS: VideoTestimonial[] = [
  {
    name: 'Gowtham - XTech Client', // ~7.8 MB
    designation: 'Operations Manager, Stadium Sports bar',
    poster: '/images/testimonials/gowtham.png',
    videoSrc:
      'https://assets.vilvabusiness.com/vilvabusiness/customers/clients/testimonials/video-testimonials/stadium-sports-bar.mp4',
  },
  {
    name: 'Arun Kumar - XTech Client', // ~12.4 MB
    designation: 'CEO & Founder, Cleanflow',
    poster: '/images/testimonials/arun-kumar.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/stall-videos/Testimonials/Testimonial%20-%20Cleanflow.mp4',
  },
  {
    name: 'Ramesh - D2C Client', // ~49.3 MB
    designation: 'CEO & Founder, BogiLife',
    poster: '/images/testimonials/ramesh.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/stall-videos/Testimonials/Testimonial%20-%20BogiLife%20-%20Subs.mp4',
  },
  {
    name: 'Jhanvi - Fintect Client', // ~65.3 MB
    designation: 'Sales Head, Caps Finserv',
    poster: '/images/testimonials/jhanvi.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/stall-videos/Testimonials/Testimonial%20-%20Capsfinserv%20-%20Subs.mp4',
  },
  {
    name: 'Ganapathi - D2C Client', // ~89.7 MB
    designation: 'CEO & Founder, Magizh Handicrafts',
    poster: '/images/testimonials/ganapathi.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/stall-videos/Testimonials/Testimonial%20-%20Magizh%20-%20Subs.mp4',
  },
  {
    name: 'Vijay Rahavan - XTech Client', // ~94.0 MB
    designation: 'CEO & Founder, Vijay Talks',
    poster: '/images/testimonials/vijay-rahavan.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/testimonials/vijay%20talks/rough%20cut/vijay%20talks%20rough.mp4',
  },
  {
    name: 'Manikandan - D2C Client', // ~98.0 MB
    designation: 'CEO & Founder, Adyarhandicrafts',
    poster: '/images/testimonials/manikandan.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/stall-videos/Testimonials/Testimonial%20-%20Adyar%20-%20Subs.mp4',
  },
  {
    name: 'Chinnaraj - EdTech Client', // ~468.1 MB — by far the largest of the 8
    designation: 'CEO & Founder, Xtragenius',
    poster: '/images/testimonials/chinnaraj.png',
    videoSrc:
      'https://blr1.digitaloceanspaces.com/vilva-backup/Marketing/social_media_management/vilva%20networks/stall-videos/Testimonials/Testimonial%20-%20Xtragenius%20-%20Subs.mp4',
  },
];

export default function VideoTestimonials() {
  const trackRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Portal the lightbox to document.body — some ancestor further up the page (a
  // backdrop-blur/transform on a section) turns "fixed" into "fixed relative to that ancestor"
  // instead of the viewport, which is why the navbar was rendering above the dark overlay
  // instead of being covered by it. Rendering outside the whole component tree sidesteps that.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Only the first two cards are visible on load — the rest sat in the DOM as real <video>
  // elements from the start, so the browser fetched all 8 clips' data immediately instead of
  // as the user actually scrolled to them. Mount a card's <video> only once it scrolls near the
  // (horizontally scrolling) track's viewport, and show a skeleton placeholder in the meantime
  // — before scrolling into view, and again while the mounted video's first frame is still
  // loading — instead of the empty/black box a not-yet-ready <video> renders as.
  const [visible, setVisible] = useState<boolean[]>(() => VIDEO_TESTIMONIALS.map((_, i) => i < 2));
  const [loaded, setLoaded] = useState<boolean[]>(() => VIDEO_TESTIMONIALS.map(() => false));

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number((entry.target as HTMLElement).dataset.index);
          setVisible((prev) => (prev[index] ? prev : prev.map((v, i) => (i === index ? true : v))));
          observer.unobserve(entry.target);
        });
      },
      { root: track, rootMargin: '200px', threshold: 0.01 }
    );

    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('[data-video-card]');
    const amount = (card?.offsetWidth ?? 320) + 24;
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const closeLightbox = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i - 1 + VIDEO_TESTIMONIALS.length) % VIDEO_TESTIMONIALS.length)),
    []
  );
  const showNext = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i + 1) % VIDEO_TESTIMONIALS.length)),
    []
  );

  useEffect(() => {
    if (activeIndex === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [activeIndex, closeLightbox, showPrev, showNext]);

  const active = activeIndex !== null ? VIDEO_TESTIMONIALS[activeIndex] : null;

  return (
    <div className="w-full">
      <div
        ref={trackRef}
        className="scrollbar-hide flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-1 py-2"
      >
        {VIDEO_TESTIMONIALS.map((testimonial, index) => (
          <button
            key={testimonial.name}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
            type="button"
            data-video-card
            data-index={index}
            onClick={() => setActiveIndex(index)}
            aria-label={`Play testimonial from ${testimonial.name}`}
            className="group relative aspect-[9/16] w-[68vw] max-w-[240px] shrink-0 snap-start overflow-hidden rounded-3xl bg-[#0D1128] text-left shadow-[0_20px_50px_-20px_rgba(9,13,40,0.4)] ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-16px_rgba(25,90,255,0.35)] sm:w-[260px] lg:w-[280px]"
          >
            {testimonial.poster ? (
              <img
                src={testimonial.poster}
                alt={testimonial.name}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <>
                {!loaded[index] && (
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 animate-pulse bg-gradient-to-br from-white/10 via-white/5 to-transparent"
                  />
                )}
                {visible[index] && (
                  <video
                    src={index < 2 ? `${testimonial.videoSrc}#t=0.001` : testimonial.videoSrc}
                    preload={index < 2 ? 'auto' : 'metadata'}
                    muted
                    playsInline
                    aria-hidden="true"
                    onLoadedData={() =>
                      setLoaded((prev) => (prev[index] ? prev : prev.map((v, i) => (i === index ? true : v))))
                    }
                    className={`h-full w-full object-cover transition-opacity duration-500 group-hover:scale-105 ${
                      loaded[index] ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                )}
              </>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="absolute h-16 w-16 animate-ping rounded-full bg-white/30" />
              <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#195AFF] shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play aria-hidden="true" className="h-6 w-6 translate-x-0.5" fill="currentColor" />
              </span>
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 pt-10">
              {(() => {
                const [personName, category] = testimonial.name.split(' - ');
                return (
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    <p className="font-Schibsted_Grotesk text-sm font-bold text-white">{personName}</p>
                    {category && (
                      <span className="rounded-full bg-[#195AFF] px-2 py-0.5 font-Schibsted_Grotesk text-[10px] font-bold uppercase tracking-wide text-white">
                        {category}
                      </span>
                    )}
                  </div>
                );
              })()}
              <p className="font-Schibsted_Grotesk text-xs text-white/75">{testimonial.designation}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          aria-label="Previous video testimonials"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1586FF] text-[#1586FF] transition-colors hover:bg-[#1586FF] hover:text-white"
        >
          <ChevronLeft aria-hidden="true" className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          aria-label="Next video testimonials"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1586FF] text-[#1586FF] transition-colors hover:bg-[#1586FF] hover:text-white"
        >
          <ChevronRight aria-hidden="true" className="h-5 w-5" />
        </button>
      </div>

      {mounted && active && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${active.name} testimonial`}
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6 sm:top-6"
          >
            <X aria-hidden="true" className="h-6 w-6" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            aria-label="Previous"
            className="absolute left-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-6"
          >
            <ChevronLeft aria-hidden="true" className="h-6 w-6" />
          </button>

          <div
            className="relative aspect-[9/16] max-h-[85vh] w-full max-w-[420px] overflow-hidden rounded-3xl bg-[#0D1128] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              key={active.videoSrc}
              className="h-full w-full object-cover"
              poster={active.poster}
              controls
              autoPlay
              playsInline
            >
              <source src={active.videoSrc} type="video/mp4" />
            </video>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 pt-12">
              <p className="font-Schibsted_Grotesk text-base font-bold text-white">{active.name}</p>
              <p className="font-Schibsted_Grotesk text-sm text-white/75">{active.designation}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label="Next"
            className="absolute right-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
          >
            <ChevronRight aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
